import { createSharedSocket } from './shared-socket'

// 订阅句柄属于页面，不属于短Token；新连接打开后才接管，旧连接的迟到帧不能回灌。
export function createRotatingSharedSocket ({ code, session, current, onInvalidated, onState = () => {},
  Socket, location, every, clear, later = setTimeout, cancel = clearTimeout, events = window }) {
  let active, candidate, sequence = 0, disposed = false, retry, backoff = 1000, credential = session, connectedBefore = false
  const entries = new Map()
  const alive = () => !disposed && current()
  const clean = connection => {
    if (!connection) return
    connection.socket.close()
    entries.forEach(entry => { entry.handles.delete(connection.id) })
  }
  const schedule = () => {
    cancel(retry)
    if (!disposed && entries.size) { retry = later(() => reconnect(), backoff); backoff = Math.min(backoff * 2, 30000) }
  }
  const attach = (connection, entry) => {
    if (entry.handles.has(connection.id)) return
    const handle = connection.socket.subscribe(entry.id, entry.topic, entry.parameter).subscribe(
      value => { if (alive() && active === connection && entries.get(entry.id) === entry) entry.next(value) },
      error => { if (!connection.failed && alive() && active === connection) entry.error(error) },
      () => { if (alive() && active === connection) entry.complete() })
    entry.handles.set(connection.id, handle)
  }
  const reconnect = () => {
    cancel(retry)
    if (!alive() || !entries.size) return
    if (candidate) clean(candidate)
    const connection = { id: ++sequence, failed: false, credential }
    candidate = connection
    onState(active && !active.socket.closed ? 'renewing' : 'connecting')
    connection.socket = createSharedSocket({ code, session: credential, authorizationVersion: 2, current: alive,
      ...(Socket ? { Socket } : {}), ...(location ? { location } : {}), ...(every ? { every } : {}), ...(clear ? { clear } : {}), later, cancel,
      onOpen: () => {
        if (!alive() || candidate !== connection) return clean(connection)
        const previous = active; active = connection; candidate = null; backoff = 1000
        clean(previous); onState('ready')
        const resumed = connectedBefore; connectedBefore = true
        // 建立新订阅后通知图表做一次有界缺口校准，不重建组件，也不重复命令。
        if (resumed) Promise.resolve().then(() => {
          if (!alive() || active !== connection) return
          entries.forEach(entry => {
            try { Promise.resolve(entry.reconnected()).catch(entry.error) } catch (cause) { entry.error(cause) }
          })
        })
      },
      onFailure: () => {
        connection.failed = true
        clean(connection)
        if (candidate === connection) candidate = null
        if (active === connection) active = null
        if (alive()) { onState('reconnecting'); schedule() }
      },
      onInvalidated: () => { if (alive()) onInvalidated(connection.credential) }
    })
    entries.forEach(entry => attach(connection, entry))
  }
  const wake = () => { if (alive() && entries.size && (!active || active.socket.closed)) reconnect() }
  events.addEventListener('online', wake)
  return {
    get closed () { return disposed },
    get ready () { return !!active && !active.socket.closed },
    replace (value) { if (credential !== value) { credential = value; reconnect() } },
    subscribe (id, topic, parameter) {
      let reconnected = () => {}
      const stream = { onReconnect (callback) { reconnected = callback; return stream }, subscribe (next, error = () => {}, complete = () => {}) {
        if (!alive()) throw new Error('分享访问已失效')
        if (entries.has(id)) throw new Error('实时订阅编号重复')
        const entry = { id, topic, parameter, next, error, complete, reconnected, handles: new Map() }
        entries.set(id, entry)
        try {
          if (active && !active.socket.closed) attach(active, entry)
          if (candidate && !candidate.socket.closed) attach(candidate, entry)
          if (!active && !candidate) reconnect()
        } catch (cause) { entries.delete(id); throw cause }
        return { unsubscribe () {
          if (entries.get(id) !== entry) return
          entries.delete(id); entry.handles.forEach(handle => handle.unsubscribe()); entry.handles.clear()
          if (!entries.size) { cancel(retry); clean(candidate); clean(active); candidate = active = null; onState('idle') }
        } }
      } }
      return stream
    },
    close () {
      disposed = true; cancel(retry); events.removeEventListener('online', wake)
      clean(candidate); clean(active); entries.clear(); candidate = active = null
    }
  }
}
